import type {
  PreferenceGroupPreferenceItem,
  PreferenceGroupTab,
  PreferenceItem,
  PreferenceLayoutNode,
  PreferenceTab,
  UserPreferenceItem,
  UserPreferenceTab,
  ValueOption,
} from '../models'

type AnyPreferenceItem = PreferenceItem | UserPreferenceItem | PreferenceGroupPreferenceItem

export function getPreferenceValueOptions(
  preference: Pick<AnyPreferenceItem, 'value_options'>
): ValueOption[] {
  return preference.value_options ?? []
}

export function getLayoutNodePreferences<TPreference extends AnyPreferenceItem>(
  layoutNode: PreferenceLayoutNode<TPreference>
): TPreference[] {
  return [...layoutNode.preferences, ...layoutNode.children.flatMap(getLayoutNodePreferences)]
}

export function getTabPreferences<TPreference extends AnyPreferenceItem>(
  tab: PreferenceTab<TPreference> | null | undefined
): TPreference[] {
  return tab?.layout_nodes.flatMap(getLayoutNodePreferences) ?? []
}

export function getAllPreferences<TPreference extends AnyPreferenceItem>(
  tabs: PreferenceTab<TPreference>[] | null | undefined
): TPreference[] {
  return tabs?.flatMap(getTabPreferences) ?? []
}

export function findPreferenceByKey<TPreference extends AnyPreferenceItem>(
  tabs: PreferenceTab<TPreference>[] | null | undefined,
  preferenceKey: string
): TPreference | null {
  return (
    getAllPreferences(tabs).find((preference) => preference.preference_key === preferenceKey) ??
    null
  )
}

function mapPreferenceLayoutNode<TPreference extends AnyPreferenceItem>(
  layoutNode: PreferenceLayoutNode<TPreference>,
  mapper: (preference: TPreference) => TPreference
): PreferenceLayoutNode<TPreference> {
  return {
    ...layoutNode,
    preferences: layoutNode.preferences.map(mapper),
    children: layoutNode.children.map((child) => mapPreferenceLayoutNode(child, mapper)),
  }
}

export function mapPreferences<TPreference extends AnyPreferenceItem>(
  tabs: PreferenceTab<TPreference>[],
  mapper: (preference: TPreference) => TPreference
): PreferenceTab<TPreference>[] {
  return tabs.map((tab) => ({
    ...tab,
    layout_nodes: tab.layout_nodes.map((layoutNode) => mapPreferenceLayoutNode(layoutNode, mapper)),
  }))
}

export type PreferenceGroupTabModel = PreferenceGroupTab
export type UserPreferenceTabModel = UserPreferenceTab
